
import { supabase } from "@/integrations/supabase/client";
import { Article } from "@/types/models";
import { extractPlainTextFromBlocks, transformArticleForClient } from "./utils";
import { articleBlockService } from "./articleBlockService";

export const articleQueryService = {
  extractPlainTextFromBlocks,
  
  async getArticleById(id: string) {
    try {
      // Validate UUID format to prevent database errors
      if (!id || id === 'undefined' || id === 'NaN' || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) {
        console.error(`Invalid article ID format: ${id}`);
        return { 
          data: null, 
          error: new Error(`Invalid article ID: ${id}`) 
        };
      }
      
      console.log(`Fetching article with ID: ${id}`);
      
      // Get the article first
      const { data: article, error: articleError } = await supabase
        .from('articles')
        .select('*')
        .eq('id', id)
        .single();

      if (articleError) {
        console.error(`Error fetching article: ${articleError.message}`);
        throw articleError;
      }

      if (!article) {
        console.error(`Article not found with ID: ${id}`);
        return {
          data: null,
          error: new Error(`Article not found with ID: ${id}`)
        };
      }

      // Get the article blocks
      const { data: blocks, error: blocksError } = await supabase
        .from('article_blocks')
        .select('*')
        .eq('article_id', id)
        .order('position');
        
      if (blocksError) {
        console.error(`Error fetching article blocks: ${blocksError.message}`);
        throw blocksError;
      }

      // Then separately get the author profile
      const { data: authorData, error: authorError } = await supabase
        .from('profiles')
        .select('id, display_name, avatar_url, bio')
        .eq('id', article.author_id)
        .single();

      if (authorError) {
        console.warn('Error fetching author profile:', authorError);
        // Continue with article data even if author fetch fails
      }

      // Map author data to the expected format
      const author = authorData ? {
        id: authorData.id,
        name: authorData.display_name,
        avatar: authorData.avatar_url,
        bio: authorData.bio,
        isFollowed: false // Default value, would be updated later if needed
      } : null;

      // Convert blocks to EditorJS format for the editor
      const editorContent = await articleBlockService.convertBlocksToEditorJS(blocks || []);
      
      // Extract plain text content for backwards compatibility
      let plainTextContent = '';
      if (editorContent && editorContent.blocks) {
        plainTextContent = extractPlainTextFromBlocks(editorContent.blocks);
      }

      // Get the article tags
      const { data: tagsData, error: tagsError } = await supabase
        .from('article_tags')
        .select(`
          tags(id, name)
        `)
        .eq('article_id', id);

      if (tagsError) {
        console.error(`Error fetching article tags: ${tagsError.message}`);
        throw tagsError;
      }

      // Extract tag names and add to article
      const tags = tagsData.map((tag: any) => tag.tags?.name).filter(Boolean);
      
      // Get article media
      const { data: mediaData, error: mediaError } = await supabase
        .from('article_media')
        .select('*')
        .eq('article_id', id);
        
      if (mediaError) {
        console.error(`Error fetching article media: ${mediaError.message}`);
        throw mediaError;
      }
      
      // Convert fields to match the expected model
      const transformedArticle = {
        ...article,
        content: plainTextContent, // For legacy components that expect string content
        cover_image: article.featured_image_url,
        read_time: article.reading_time,
        access_type: article.payment_type,
        price: article.micropayment_price,
        views: article.view_count, // Map view_count to views for backward compatibility
        editor_data: editorContent, // Store the structured content for editor
        blocks: blocks || [], // Raw blocks for direct access
        tags,
        media: mediaData || [],
        author // Add the properly formatted author data
      };
      
      console.log('Successfully transformed article data');
      
      return { 
        data: transformedArticle, 
        error: null 
      };
    } catch (error) {
      console.error('Error getting article:', error);
      return { data: null, error };
    }
  },

  async getArticlesByAuthor(authorId: string) {
    try {
      const { data: articles, error } = await supabase
        .from('articles')
        .select('*')
        .eq('author_id', authorId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Transform articles to include block data
      const transformedArticles = await Promise.all(
        articles.map(async (article: Article) => {
          // Get blocks for this article
          const { data: blocks } = await supabase
            .from('article_blocks')
            .select('*')
            .eq('article_id', article.id)
            .order('position');
            
          // Convert blocks to EditorJS format
          const editorData = await articleBlockService.convertBlocksToEditorJS(blocks || []);
          
          // Extract plain text
          const plainTextContent = extractPlainTextFromBlocks(editorData.blocks || []);
          
          return {
            ...article,
            content: plainTextContent,
            cover_image: article.featured_image_url,
            read_time: article.reading_time,
            access_type: article.payment_type,
            price: article.micropayment_price,
            views: article.view_count,
            editor_data: editorData,
            blocks: blocks || []
          };
        })
      );
      
      return { data: transformedArticles, error: null };
    } catch (error) {
      console.error('Error getting articles by author:', error);
      return { data: null, error };
    }
  },
  
  async getArticlesByTag(tagName: string, limit = 20) {
    try {
      console.log(`Fetching articles with tag: ${tagName}`);
      
      // Get tag ID first
      const { data: tagData, error: tagError } = await supabase
        .from('tags')
        .select('id')
        .eq('name', tagName)
        .single();
        
      if (tagError) {
        console.error(`Error fetching tag: ${tagError.message}`);
        throw tagError;
      }
      
      if (!tagData) {
        console.error(`Tag not found: ${tagName}`);
        return { data: [], error: new Error(`Tag not found: ${tagName}`) };
      }
      
      // Get articles with this tag
      const { data: articleTagsData, error: articleTagsError } = await supabase
        .from('article_tags')
        .select(`
          article_id,
          articles(*)
        `)
        .eq('tag_id', tagData.id)
        .limit(limit);
        
      if (articleTagsError) {
        console.error(`Error fetching article tags: ${articleTagsError.message}`);
        throw articleTagsError;
      }
      
      // Extract the article data from the join
      const articles = articleTagsData
        .filter(item => item.articles) // Filter out any null articles
        .map(item => item.articles);
    
      // Fetch author data for these articles
      const authorIds = [...new Set(articles.map(article => article.author_id))];
      
      let authorProfiles = new Map();
      if (authorIds.length > 0) {
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('id, display_name, avatar_url, bio')
          .in('id', authorIds);
          
        if (!profilesError && profilesData) {
          profilesData.forEach(profile => {
            authorProfiles.set(profile.id, profile);
          });
        }
      }
      
      // Fetch tags for each article
      const transformedArticles = await Promise.all(
        articles.map(async (article) => {
          // Get article tags
          const { data: tagsData } = await supabase
            .from('article_tags')
            .select(`
              tags(id, name)
            `)
            .eq('article_id', article.id);

          const tags = tagsData ? tagsData.map((tag: any) => tag.tags?.name).filter(Boolean) : [];
          
          // Get author profile
          const authorProfile = authorProfiles.get(article.author_id);
          
          // Add author data to the article
          const articleWithAuthor = {
            ...article,
            tags,
            author: authorProfile
          };
          
          // Transform article to match expected format
          return transformArticleForClient(articleWithAuthor);
        })
      );
      
      return { data: transformedArticles, error: null };
    } catch (error) {
      console.error('Error getting articles by tag:', error);
      return { data: null, error };
    }
  }
};
